import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  IconButton,
  Divider,
  Alert,
  FormControlLabel,
  Checkbox,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  CloudUpload as UploadIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import DigitalSignature from '../../common/DigitalSignature';
import { RootState } from 'src/redux-store/appStore';
import { useSelector } from 'react-redux';
import { httpRequest } from 'src/services/httpRequest';
import LoadingOverlay from 'src/components/common/LoadingOverlay';

interface CertificateData {
  townshipAddress: string;
  dateIssued: string;
  stateCode: string;
  permitNumber: string;
  projectType: string;
  propertyAddress: string;
  block: string;
  lot: string;
  ownerName: string;
  contractor: string;
  constructionOfficialName: string;
  constructionOfficialSignDate: string;
  townshipName: string;
}

interface CertificateModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: (data: CertificateData, signature: string) => Promise<void>;
  applicationData?: any;
  onApplicationClosed?: () => void;
  handleGetApplicationDetails?: () => void;
}

const CertificateOfApproval: React.FC<CertificateModalProps> = ({
  open,
  onClose,
  applicationData,
  onApplicationClosed,
  handleGetApplicationDetails
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const userDetails = useSelector((store: RootState) => store.user.userDetails);

  // State management
  const [currentStep, setCurrentStep] = useState<'confirmation' | 'certificate'>('confirmation');
  const [generateCertificate, setGenerateCertificate] = useState(false);
  const [signature, setSignature] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const certificateRef = useRef<HTMLDivElement>(null);

  const formData: CertificateData = {
    townshipAddress: userDetails?.TownshipAddress || '',
    dateIssued: new Date().toLocaleDateString(),
    stateCode: 'NJAC 5:23-2.23',
    permitNumber: applicationData?.ApplicationNumber || '',
    projectType: applicationData?.ApplicationTitle || '',
    propertyAddress: applicationData?.ProjectLocation || '',
    block: applicationData?.BlockLot || '',
    lot: '',
    ownerName: applicationData?.OwnersName || '',
    contractor: '',
    constructionOfficialName: userDetails?.FirstName + " " + userDetails?.LastName || '',
    constructionOfficialSignDate: new Date().toLocaleDateString(),
    townshipName: userDetails?.TownshipName || '',
  };

  const handleSignatureChange = (signatureData: string | null) => {
    setSignature(signatureData);
  };

  const handleGenerateCertificateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const shouldGenerate = event.target.checked;
    setGenerateCertificate(shouldGenerate);

    if (shouldGenerate) {
      setCurrentStep('certificate');
    } else {
      setCurrentStep('confirmation');
      setSignature(null);
    }
  };

  const validateCertificateForm = (): boolean => {
    if (!generateCertificate) return true;

    if (!signature) {
      setError('Digital signature is required when generating certificate');
      return false;
    }

    const requiredFields = [
      { field: 'townshipAddress', label: 'Township Address' },
      { field: 'permitNumber', label: 'Permit Number' },
      { field: 'projectType', label: 'Project Type' },
      { field: 'propertyAddress', label: 'Property Address' },
      { field: 'ownerName', label: 'Owner Name' },
      { field: 'townshipName', label: 'Township Name' },
    ];

    for (const { field, label } of requiredFields) {
      if (!formData[field as keyof CertificateData]?.trim()) {
        setError(`${label} is required but missing from application data`);
        return false;
      }
    }

    setError(null);
    return true;
  };

  const generatePDF = async (): Promise<Blob> => {
    if (!certificateRef.current) throw new Error('Certificate element not found');

    const canvas = await html2canvas(certificateRef.current, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgWidth = 210;
    const pageHeight = 295;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;

    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    return pdf.output('blob');
  };

  const uploadPDFToServer = async (pdfBlob: Blob): Promise<string> => {
    const formData1 = new FormData();
    formData1.append('File', pdfBlob, `certificate-${applicationData?.ApplicationNumber || 'approval'}.pdf`);

    const uploadResponse = await httpRequest(
      'POST',
      '/uploadPDF/document_Images?PermitName=Construction&FileType=COA',
      formData1,
      {
        'Content-Type': 'multipart/form-data',
      }
    );

    return uploadResponse.data?.DownloadURL || uploadResponse.url || uploadResponse;
  };

  const closeApplication = async (certificateUrl?: string) => {
    const requestBody: any = {
      ApplicationID: applicationData?.ApplicationID || applicationData?.id,
    };

    if (certificateUrl) {
      requestBody.CertificateOfApprovalPdfUrl = certificateUrl;
    }

    await httpRequest(
      'POST',
      '/construction/closeApplication',
      requestBody
    );
  };

  const handleDownload = async () => {
    if (!validateCertificateForm()) return;

    setIsLoading(true);
    setError(null);

    try {
      const pdfBlob = await generatePDF();
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificate-${formData.permitNumber || 'approval'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSuccess('Certificate downloaded successfully!');
    } catch (err) {
      setError('Failed to generate PDF');
      console.error('PDF generation error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
      // Reset all state
      setCurrentStep('confirmation');
      setGenerateCertificate(false);
      setSignature(null);
      setError(null);
      setSuccess(null);
    }
  };

  const handleCloseApplication = async () => {
    if (!validateCertificateForm()) return;

    setIsLoading(true);
    setError(null);

    try {
      let certificateUrl: string | undefined;

      if (generateCertificate && signature) {
        // Generate PDF and upload to server
        const pdfBlob = await generatePDF();
        certificateUrl = await uploadPDFToServer(pdfBlob);
      }
      // Close the application
      await closeApplication(certificateUrl);

      setSuccess(
        generateCertificate
          ? 'Application closed successfully with certificate generated!'
          : 'Application closed successfully!'
      );

      // Call parent callback if provided
      if (onApplicationClosed) {
        onApplicationClosed();
      }

      await handleGetApplicationDetails()

      // Close modal after short delay
      setTimeout(() => {
        handleClose();
      }, 2000);

    } catch (err) {
      setError('Failed to close application. Please try again.');
      console.error('Close application error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToConfirmation = () => {
    setCurrentStep('confirmation');
    setGenerateCertificate(false);
    setSignature(null);
    setError(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={currentStep === 'certificate' ? (isMobile ? 'sm' : 'lg') : 'md'}
        fullWidth
        fullScreen={isSmallScreen && currentStep === 'certificate'}
        PaperProps={{
          sx: {
            minHeight: {
              xs: currentStep === 'certificate' ? '100vh' : 'auto',
              md: currentStep === 'certificate' ? '90vh' : 'auto'
            },
            margin: {
              xs: currentStep === 'certificate' ? 0 : 2,
              md: 2
            }
          }
        }}
      >
        {isLoading && <LoadingOverlay />}
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          pb: { xs: 1, sm: 2 }
        }}>
          <Typography
            variant={isSmallScreen ? "h6" : "h5"}
            component="div"
            sx={{
              fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
              lineHeight: 1.2
            }}
          >
            {currentStep === 'confirmation' ? 'Close Application' : 'Certificate of Approval'}
          </Typography>
          <IconButton onClick={handleClose} disabled={isLoading}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers={!isMobile}
          sx={{
            p: { xs: 1, sm: 2, md: 3 },
            overflow: currentStep === 'certificate' && isMobile ? 'auto' : 'visible',
            pb: { xs: 1, sm: 2, md: 3 }
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {currentStep === 'confirmation' && (
            <Box>
              <Paper sx={{
                p: { xs: 2, sm: 3 },
                bgcolor: '#f8f9fa',
                border: '1px solid #e9ecef'
              }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                  textAlign: { xs: 'center', sm: 'left' }
                }}>
                  <WarningIcon sx={{
                    color: 'warning.main',
                    mr: { xs: 0, sm: 2 },
                    mb: { xs: 1, sm: 0 },
                    fontSize: { xs: 28, sm: 32 }
                  }} />
                  <Typography
                    variant="h6"
                    color="warning.main"
                    sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                  >
                    Application Close Out Confirmation
                  </Typography>
                </Box>

                <Typography
                  variant="body1"
                  sx={{
                    mb: 3,
                    lineHeight: 1.6,
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  }}
                >
                  Are you sure you want to close out this application?
                </Typography>

                <Box sx={{
                  bgcolor: '#fff3cd',
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: 1,
                  border: '1px solid #ffeaa7',
                  mb: 3
                }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                  >
                    <strong>Application Details:</strong><br />
                    Application Number: {applicationData?.ApplicationNumber}<br />
                    Project Type: {applicationData?.ApplicationTitle}<br />
                    Owner: {applicationData?.OwnersName}
                  </Typography>
                </Box>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={generateCertificate}
                      onChange={handleGenerateCertificateChange}
                      color="primary"
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                    >
                      Generate Certificate of Approval for this application
                    </Typography>
                  }
                  sx={{ mb: 2 }}
                />

                {generateCertificate && (
                  <Alert severity="info" sx={{ mt: 2 }} icon={<CheckCircleIcon />}>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                    >
                      <strong>Important:</strong> Kindly make sure all inspections against this application have been completed before generating the certificate of approval.
                    </Typography>
                  </Alert>
                )}
              </Paper>
            </Box>
          )}

          {currentStep === 'certificate' && (
            <Grid container spacing={{ xs: 2, md: 3 }}>
              {/* Application Data Display Section */}
              <Grid item xs={12} lg={6}>
                <Typography
                  variant="h6"
                  gutterBottom
                  color="primary"
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                >
                  Application Information
                </Typography>

                <Box sx={{
                  bgcolor: '#f5f5f5',
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: 2,
                  mb: 3
                }}>
                  <Grid container spacing={{ xs: 1, sm: 2 }}>
                    <Grid item xs={12}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        <strong>Township:</strong> {formData.townshipName || 'Not specified'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        <strong>Address:</strong> {formData.townshipAddress || 'Not specified'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        <strong>Permit #:</strong> {formData.permitNumber || 'Not specified'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        <strong>Project Type:</strong> {formData.projectType || 'Not specified'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        <strong>Property:</strong> {formData.propertyAddress || 'Not specified'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        <strong>Block/Lot:</strong> {formData.block || 'Not specified'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        <strong>Owner:</strong> {formData.ownerName || 'Not specified'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        <strong>Official:</strong> {formData.constructionOfficialName || 'Not specified'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Digital Signature Section */}
                <DigitalSignature onSignatureChange={handleSignatureChange} />
              </Grid>

              {/* Certificate Preview Section */}
              <Grid item xs={12} lg={6}>
                <Typography
                  variant="h6"
                  gutterBottom
                  color="primary"
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                >
                  Certificate Preview
                </Typography>

                <Box
                  ref={certificateRef}
                  sx={{
                    bgcolor: '#f4f6f8',
                    p: { xs: 1, sm: 2 },
                    borderRadius: 2,
                    minHeight: { xs: '600px', sm: '700px', md: '800px' },
                    transform: {
                      xs: 'scale(0.6)',
                      sm: 'scale(0.7)',
                      md: 'scale(0.8)',
                      lg: 'scale(0.8)'
                    },
                    transformOrigin: 'top left',
                    width: {
                      xs: '166%',
                      sm: '142%',
                      md: '125%',
                      lg: '125%'
                    },
                    overflow: 'hidden'
                  }}
                >
                  {/* Certificate Content */}
                  <Box
                    sx={{
                      bgcolor: 'white',
                      p: { xs: 3, sm: 4 },
                      borderRadius: 2,
                      boxShadow: 2,
                      fontFamily: 'Helvetica Neue, Arial, sans-serif',
                    }}
                  >
                    {/* Header */}
                    <Box textAlign="center" borderBottom="2px solid #3b7789" pb={1} mb={2}>
                      <Typography
                        variant="h5"
                        color="#223354"
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}
                      >
                        Building Or Construction Department
                      </Typography>
                      <Typography
                        variant="body1"
                        color="#555"
                        sx={{
                          fontSize: { xs: '0.9rem', sm: '1rem' },
                          wordBreak: 'break-word'
                        }}
                      >
                        {formData.townshipAddress || '[Township Address]'}
                      </Typography>
                    </Box>

                    {/* Title */}
                    <Box textAlign="center" my={2}>
                      <Typography
                        variant="h6"
                        color="#3b7789"
                        sx={{
                          borderBottom: '2px solid #3b7789',
                          display: 'inline-block',
                          px: 2,
                          py: 1,
                          fontSize: { xs: '1rem', sm: '1.25rem' }
                        }}
                      >
                        CERTIFICATE OF APPROVAL
                      </Typography>
                    </Box>

                    {/* Date */}
                    <Box textAlign="right" mb={2}>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        <strong>Date Issued:</strong> {formData.dateIssued}
                      </Typography>
                    </Box>

                    {/* Statement */}
                    <Typography
                      variant="body1"
                      textAlign="center"
                      my={2}
                      lineHeight={1.8}
                      sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                    >
                      This certifies that all required inspections have been completed and approved in accordance with the{' '}
                      <strong>{formData.stateCode || '[State Code]'}</strong>, and that the construction described below
                      has been found to be in compliance with the approved plans and specifications
                    </Typography>

                    {/* Project Information */}
                    <Box
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        p: { xs: 1.5, sm: 2 },
                        my: 2,
                        bgcolor: '#f9f9f9',
                      }}
                    >
                      <Typography
                        variant="h6"
                        color="#3b7789"
                        gutterBottom
                        sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                      >
                        PROJECT INFORMATION
                      </Typography>

                      <Grid container spacing={{ xs: 1, sm: 2 }}>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                          >
                            <strong style={{ color: '#3b7789' }}>Permit Number:</strong><br />
                            {formData.permitNumber || '[Permit Number]'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                          >
                            <strong style={{ color: '#3b7789' }}>Project Type:</strong><br />
                            {formData.projectType || '[Project Type]'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: { xs: '0.8rem', sm: '0.875rem' },
                              wordBreak: 'break-word'
                            }}
                          >
                            <strong style={{ color: '#3b7789' }}>Property Address:</strong><br />
                            {formData.propertyAddress || '[Property Address]'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                          >
                            <strong style={{ color: '#3b7789' }}>Block & Lot:</strong><br />
                            Block/Lot: {formData.block || '[Block]'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                          >
                            <strong style={{ color: '#3b7789' }}>Owner Name:</strong><br />
                            {formData.ownerName || '[Owner Name]'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                          >
                            <strong style={{ color: '#3b7789' }}>Contractor:</strong><br />
                            {formData.contractor || '[Contractor]'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Signature Section */}
                    <Box textAlign="right" mt={4}>
                      <Box
                        sx={{
                          borderBottom: '1px solid black',
                          height: { xs: '40px', sm: '50px', md: '60px' },
                          width: { xs: '200px', sm: '250px', md: '300px' },
                          ml: 'auto',
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {signature && (
                          <img
                            src={signature}
                            alt="Digital Signature"
                            style={{
                              maxHeight: isMobile ? '35px' : '50px',
                              maxWidth: '90%',
                              objectFit: 'contain'
                            }}
                          />
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        {formData.constructionOfficialName || '[Construction Official Name]'}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontStyle="italic"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        Construction Official
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        {formData.constructionOfficialSignDate}
                      </Typography>
                    </Box>

                    {/* Footer */}
                    <Box textAlign="center" mt={4} pt={2} borderTop="1px solid #e0e0e0">
                      <Typography
                        variant="body2"
                        color="#666"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        This certificate must be retained as a permanent record.<br />
                        Issued by the Township of {formData.townshipName || '[Township Name]'} on {formData.dateIssued}.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{
          p: { xs: 1.5, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 1 },
          // alignItems: 'stretch',
          justifyContent: { xs: 'stretch', sm: 'flex-end' },
          borderTop: { xs: '1px solid #e0e0e0', sm: 'none' }
        }}>
          {currentStep === 'confirmation' && (
            <>
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={isLoading}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  order: { xs: 2, sm: 1 }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                onClick={handleCloseApplication}
                disabled={isLoading}
                startIcon={<UploadIcon />}
                // color={generateCertificate ? 'primary' : 'warning'}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  fontSize: { xs: '0.85rem', sm: '0.875rem' },
                  order: { xs: 1, sm: 2 }
                }}
              >
                {generateCertificate ? 'Generate Certificate & Close' : 'Close Application'}
              </Button>

            </>
          )}

          {currentStep === 'certificate' && (
            <>
              <Button
                variant="outlined"
                onClick={handleCloseApplication}
                disabled={!signature}
                startIcon={<UploadIcon />}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  fontSize: { xs: '0.85rem', sm: '0.875rem' },
                  order: { xs: 1, sm: 4 }
                }}
              >
                Generate Certificate & Close Application
              </Button>
              <Button
                variant="outlined"
                onClick={handleDownload}
                disabled={!signature}
                startIcon={<DownloadIcon />}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  fontSize: { xs: '0.85rem', sm: '0.875rem' },
                  order: { xs: 2, sm: 3 }
                }}
              >
                Download PDF
              </Button>
              <Button
                variant="outlined"
                onClick={handleBackToConfirmation}
                disabled={isLoading}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  order: { xs: 3, sm: 2 }
                }}
              >
                Back
              </Button>
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={isLoading}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  order: { xs: 4, sm: 1 }
                }}
              >
                Cancel
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CertificateOfApproval;